'use client';

import { useState } from 'react';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import { api } from '@/lib/api';
import InputPassword from '@/components/InputPassword';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';

// Cambio de la PROPIA contraseña del usuario en sesión (avatar → «Cambiar
// contraseña»). Exige la contraseña actual; todos los campos llevan ojito.
export default function CambiarMiContrasena({ onVolver }: { onVolver?: () => void }) {
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [guardando, setGuardando] = useState(false);

  // Errores en vivo, en el campo (mismo lenguaje de validación del resto de la app).
  const errorNueva = nueva && nueva.length < 8 ? 'Debe tener al menos 8 caracteres.' : null;
  const errorConfirmar = confirmar && confirmar !== nueva ? 'Las contraseñas no coinciden.' : null;
  const lista = !!actual && nueva.length >= 8 && confirmar === nueva;

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (!lista) return;
    setGuardando(true);
    try {
      await api('/perfil/password', {
        method: 'POST',
        body: { passwordActual: actual, nuevaPassword: nueva },
      });
      avisoExito('Contraseña actualizada.', 'Úsela en su próximo ingreso.');
      setActual(''); setNueva(''); setConfirmar('');
      onVolver?.();
    } catch (e) {
      avisoError('No se pudo cambiar la contraseña', e instanceof Error ? e.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="mx-auto max-w-md space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="size-5 text-primary" /> Cambiar contraseña
          </CardTitle>
          <CardDescription>
            Confirme su contraseña actual y defina la nueva (mínimo 8 caracteres).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={guardar} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pass-actual">Contraseña actual</Label>
              <InputPassword id="pass-actual" autoComplete="current-password"
                value={actual} onChange={(e) => setActual(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pass-nueva">Nueva contraseña</Label>
              <InputPassword id="pass-nueva" autoComplete="new-password" error={errorNueva}
                value={nueva} onChange={(e) => setNueva(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pass-confirmar">Confirmar nueva contraseña</Label>
              <InputPassword id="pass-confirmar" autoComplete="new-password" error={errorConfirmar}
                value={confirmar} onChange={(e) => setConfirmar(e.target.value)} required />
            </div>
            <Button type="submit" className="w-full" disabled={!lista || guardando}>
              {guardando ? 'Guardando…' : 'Cambiar contraseña'}
            </Button>
          </form>

          <p className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="size-3.5" />
            Si la olvidó, use «Recuperar contraseña» en la pantalla de ingreso.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
